import { Component } from '@theme/component';

const STORAGE_KEY = 'upupa:favourites:v1';
const isHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);
const safeItems = (value) => Array.isArray(value) ? value.filter(item => item && isHandle(item.handle) && typeof item.title === 'string').slice(0, 200).map(item => ({ handle: item.handle, title: item.title.slice(0, 500) })) : [];

/**
 * @typedef {object} FavouriteRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLUListElement} list
 * @property {HTMLElement} empty
 * @property {HTMLElement} status
 * @property {HTMLElement} dialogStatus
 * @property {HTMLElement} linkFallback
 * @property {HTMLInputElement} shareInput
 * @property {HTMLScriptElement} labels
 * @property {HTMLTemplateElement} heartIcon
 * @property {HTMLTemplateElement} shareIcon
 * @extends {Component<FavouriteRefs>}
 */
class UpupaFavourites extends Component {
  requiredRefs = ['dialog', 'list', 'empty', 'status', 'dialogStatus', 'linkFallback', 'shareInput', 'labels', 'heartIcon', 'shareIcon'];
  #items = new Map();
  #titles = new Map();
  #labels = {};
  #abort;
  #requests;
  #observer;
  #timer;
  #opener;
  #sessionOnly = false;

  connectedCallback() {
    super.connectedCallback();
    this.#labels = JSON.parse(this.refs.labels.textContent);
    this.#abort = new AbortController();
    try { this.#items = new Map(safeItems(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')).map(item => [item.handle, item])); }
    catch { this.#sessionOnly = true; }
    document.addEventListener('click', this.#click, { capture: true, signal: this.#abort.signal });
    window.addEventListener('storage', this.#storage, { signal: this.#abort.signal });
    this.refs.dialog.addEventListener('close', () => { this.#requests?.abort(); this.#opener?.focus(); }, { signal: this.#abort.signal });
    this.#observer = new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) {
        if (node instanceof Element && !this.contains(node)) this.#enhance(node);
      }
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
    this.#enhance(document);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#requests?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    this.refs.dialog?.close();
  }

  #url(handle) {
    const root = this.dataset.root.replace(/\/?$/, '/');
    return new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
  }

  #enhance(root) {
    const matches = (selector) => [...(root instanceof Element && root.matches(selector) ? [root] : []), ...root.querySelectorAll(selector)];
    for (const group of matches('[data-upupa-actions]')) {
      if (!isHandle(group.dataset.handle)) continue;
      const title = group.dataset.title || group.dataset.handle;
      this.#titles.set(group.dataset.handle, title);
      group.hidden = false;
      this.#syncGroup(group);
    }
    for (const button of matches('[data-upupa-open-favourites]')) { button.hidden = false; this.#syncHeader(button); }
  }

  #syncGroup(group) {
    const saved = this.#items.has(group.dataset.handle);
    const button = group.querySelector('[data-upupa-favourite]');
    if (!button) return;
    const label = saved ? this.#labels.remove : this.#labels.save;
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', `${label}: ${group.dataset.title}`);
    button.title = label;
  }

  #syncHeader(button) {
    const count = button.querySelector('[data-upupa-favourites-count]');
    if (count) { count.textContent = String(this.#items.size); count.hidden = !this.#items.size; }
    button.setAttribute('aria-label', `${this.#labels.title} (${this.#items.size})`);
  }

  #sync() {
    document.querySelectorAll('[data-upupa-actions]').forEach(group => this.#syncGroup(group));
    document.querySelectorAll('[data-upupa-open-favourites]').forEach(button => this.#syncHeader(button));
  }

  #storage = (event) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    try { this.#items = new Map(safeItems(JSON.parse(event.newValue || '[]')).map(item => [item.handle, item])); }
    catch { return; }
    this.#sync();
    if (this.refs.dialog.open) this.#renderList();
  };

  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-upupa-favourite], [data-upupa-share], [data-upupa-open-favourites], [data-upupa-close]');
    if (!button) {
      if (event.target === this.refs.dialog) {
        const rect = this.refs.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.refs.dialog.close();
      }
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.hasAttribute('data-upupa-close')) { this.refs.dialog.close(); return; }
    if (button.hasAttribute('data-upupa-open-favourites')) { this.#open(button); return; }
    const group = button.closest('[data-upupa-actions]');
    if (!group || !isHandle(group.dataset.handle)) return;
    if (button.hasAttribute('data-upupa-share')) { void this.#share(group, button); return; }
    const handle = group.dataset.handle;
    const removing = this.#items.has(handle);
    if (!removing && this.#items.size >= 200) { this.#announce(this.#labels.limit); return; }
    const inList = this.refs.list.contains(button);
    const index = inList ? [...this.refs.list.children].indexOf(group.closest('li')) : -1;
    if (removing) this.#items.delete(handle);
    else this.#items.set(handle, { handle, title: group.dataset.title || handle });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.#items.values()])); }
    catch { this.#sessionOnly = true; }
    this.#sync();
    if (this.refs.dialog.open) this.#renderList();
    if (inList) (this.refs.list.children[Math.min(index, this.refs.list.children.length - 1)]?.querySelector('[data-upupa-favourite]') || this.refs.dialog.querySelector('[data-upupa-close]')).focus();
    this.#announce(this.#sessionOnly ? this.#labels.session_only : (removing ? this.#labels.removed : this.#labels.saved));
  };

  #announce(text) {
    clearTimeout(this.#timer);
    this.refs.status.textContent = '';
    this.refs.dialogStatus.textContent = '';
    (this.refs.dialog.open ? this.refs.dialogStatus : this.refs.status).textContent = text;
    this.#timer = setTimeout(() => { this.refs.status.textContent = ''; this.refs.dialogStatus.textContent = ''; }, 3500);
  }

  #actions(item) {
    const group = document.createElement('div');
    group.className = 'upupa-product-actions';
    group.dataset.upupaActions = '';
    group.dataset.handle = item.handle;
    group.dataset.title = this.#titles.get(item.handle) || item.title;
    for (const type of ['favourite', 'share']) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'upupa-social-button';
      button.setAttribute(`data-upupa-${type}`, '');
      button.setAttribute('aria-label', `${type === 'share' ? this.#labels.share : this.#labels.remove}: ${group.dataset.title}`);
      button.title = type === 'share' ? this.#labels.share : this.#labels.remove;
      button.append((type === 'share' ? this.refs.shareIcon : this.refs.heartIcon).content.cloneNode(true));
      group.append(button);
    }
    this.#syncGroup(group);
    return group;
  }

  #renderList() {
    const fragment = document.createDocumentFragment();
    for (const item of this.#items.values()) {
      const row = document.createElement('li'); row.className = 'upupa-favourites-item'; row.dataset.handle = item.handle;
      const link = document.createElement('a'); link.href = this.#url(item.handle).href;
      link.textContent = this.#titles.get(item.handle) || item.title;
      row.append(link, this.#actions(item)); fragment.append(row);
    }
    this.refs.list.replaceChildren(fragment);
    this.refs.empty.hidden = this.#items.size > 0;
  }

  #open(button) {
    this.#opener = button;
    this.refs.linkFallback.hidden = true;
    this.#renderList();
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.#requests?.abort(); this.#requests = new AbortController();
    const signal = this.#requests.signal;
    for (const item of this.#items.values()) {
      if (this.#titles.has(item.handle)) continue;
      const url = this.#url(item.handle); url.pathname += '.js';
      fetch(url, { signal, headers: { Accept: 'application/json' } })
        .then(response => { if (!response.ok) throw new Error('Unavailable'); return response.json(); })
        .then(product => {
          if (signal.aborted || typeof product.title !== 'string') return;
          this.#titles.set(item.handle, product.title);
          const row = [...this.refs.list.children].find(element => element.dataset.handle === item.handle);
          if (!row) return;
          row.querySelector('a').textContent = product.title;
          const group = row.querySelector('[data-upupa-actions]'); group.dataset.title = product.title; this.#syncGroup(group);
          group.querySelector('[data-upupa-share]').setAttribute('aria-label', `${this.#labels.share}: ${product.title}`);
        }).catch(() => {});
    }
  }

  async #share(group, button) {
    const url = this.#url(group.dataset.handle).href;
    const title = group.dataset.title;
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; }
      catch (error) { if (error.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(url); this.#announce(this.#labels.copied); }
    catch {
      if (!this.refs.dialog.open) this.#open(button);
      this.refs.linkFallback.hidden = false;
      this.refs.shareInput.value = url;
      this.refs.shareInput.focus(); this.refs.shareInput.select();
    }
  }
}

if (!customElements.get('upupa-favourites')) customElements.define('upupa-favourites', UpupaFavourites);
